/**
 * "Which providers / game types may I play right now" — the promotion the player
 * is currently working off can forbid a camp or a whole game type.
 *
 * The server refuses such a launch or bet outright (GAME-017). This exists so
 * the player is told BEFORE they tap: a card that opens and then throws is the
 * same defect QA filed, only one step later. The lock is a hint, never the
 * control — svc-game is the control.
 *
 * `null` on a dimension means nothing restricts it. `[]` is a different answer
 * (two live bonuses whose allow-lists do not overlap) and means nothing on that
 * dimension is playable — see the contract note in svc-promo's repo module.
 */
import { useQuery } from '@tanstack/react-query';
import { apiFetch } from '../../lib/api/client.js';
import { useAuthStore } from '../../stores/auth.js';
import { PROMO_QUERY_KEYS } from './api.js';

export interface PlayRestrictions {
  restricted: boolean;
  allowedProviders: string[] | null;
  allowedGameTypes: string[] | null;
}

/**
 * The answer for a client that has not asked, or asked and failed. Unrestricted,
 * because a lobby that locks every card when svc-promo is unreachable is worse
 * than one that lets a player tap a card the server will refuse with a message.
 * It is the same fail-open direction svc-game's gate takes for the same outage.
 */
export const NO_RESTRICTIONS: PlayRestrictions = {
  restricted: false,
  allowedProviders: null,
  allowedGameTypes: null,
};

export async function fetchPlayRestrictions(): Promise<PlayRestrictions> {
  const raw = await apiFetch<Partial<PlayRestrictions>>('/promotion-claims/me/play-restrictions');
  // Normalised, not trusted: this SPA and svc-promo deploy on independent
  // pipelines, so a build that answers a partial shape must read as "nothing
  // restricted" and never as "nothing allowed".
  return {
    restricted: Boolean(raw?.restricted),
    allowedProviders: Array.isArray(raw?.allowedProviders) ? raw.allowedProviders : null,
    allowedGameTypes: Array.isArray(raw?.allowedGameTypes) ? raw.allowedGameTypes : null,
  };
}

export interface LockableGame {
  providerId?: string | undefined;
  gameType?: string | null | undefined;
}

/**
 * True when this game may not be played under the member's live promotions.
 *
 * A game whose provider is unknown to the caller (mock data, a card rendered
 * before the catalog resolved) is never locked: the lobby must not invent a
 * refusal the server would not make. An unknown GAME TYPE against a
 * type-restricted promotion IS locked, because that is exactly what svc-game
 * answers for it.
 */
export function isGameLockedByPromo(
  restrictions: PlayRestrictions | undefined | null,
  game: LockableGame,
): boolean {
  if (!restrictions?.restricted) return false;
  const { allowedProviders, allowedGameTypes } = restrictions;
  if (allowedProviders !== null && game.providerId) {
    if (!allowedProviders.includes(game.providerId)) return true;
  }
  if (allowedGameTypes !== null) {
    if (!game.gameType || !allowedGameTypes.includes(game.gameType)) return true;
  }
  return false;
}

/**
 * Live restrictions for the signed-in member. Anonymous browsing never asks.
 *
 * One shared query key, so the dozens of cards a lobby renders resolve to a
 * single request. 60s stale time matches svc-game's own cache on the gate, so
 * the lock a player sees and the refusal they would hit go stale together — and
 * `invalidateClaimState` cuts that window to zero at the moment a claim is
 * created or ends, which is the only moment the answer can actually change.
 */
export interface PlayRestrictionsOptions {
  /**
   * Poll the shared query this often, in ms. For a surface that must notice the
   * flip WITHOUT a navigation or a claim of its own — an open provider frame,
   * where the restriction can start mid-session and nothing pushes it in
   * (#546). Omitted everywhere else: the lobby learns the answer when it asks.
   * React Query takes the shortest interval among the observers of a key, so
   * one polling subscriber refreshes the verdict for every card on the page.
   */
  refetchInterval?: number;
}

export function usePlayRestrictions(options?: PlayRestrictionsOptions): PlayRestrictions {
  const authed = useAuthStore((s) => !!s.accessToken);
  const { data } = useQuery({
    queryKey: PROMO_QUERY_KEYS.playRestrictions,
    queryFn: fetchPlayRestrictions,
    enabled: authed,
    staleTime: 60_000,
    retry: false,
    refetchInterval: options?.refetchInterval ?? false,
  });
  return data ?? NO_RESTRICTIONS;
}
